export interface RankingEntry {
  name: string;
  score: number;
}

export interface RankingSnapshot {
  entries: RankingEntry[];
  placement: number;
}

const MAX_ENTRIES = 10;
const STORAGE_KEYS = {
  nickname: 'wombat.playerName',
  ranking: 'wombat.rankingCache',
} as const;

export class RankingService {
  private nickname = 'Anonymous';
  private entries: RankingEntry[] = [];
  private readonly endpoint: string;
  private readonly hasStorage = typeof window !== 'undefined' && !!window.localStorage;

  constructor(endpoint = '/api/ranking') {
    this.endpoint = endpoint;
    this.restoreFromStorage();
  }

  getNickname(): string {
    return this.nickname;
  }

  setNickname(raw: string): void {
    this.nickname = this.cleanName(raw);
    this.persistNickname();
  }

  getEntries(): RankingEntry[] {
    return [...this.entries];
  }

  async refreshEntries(): Promise<RankingEntry[]> {
    try {
      const response = await fetch(this.endpoint);
      if (!response.ok) {
        throw new Error('Failed to fetch ranking');
      }
      const payload = await response.json();
      const incoming = Array.isArray(payload.entries) ? payload.entries : [];
      this.entries = this.normalizeEntries(incoming);
      this.persistEntries();
    } catch {
      // Keep existing cache
    }
    return this.getEntries();
  }

  async submitScore(score: number, overrideName?: string): Promise<RankingSnapshot> {
    const entry: RankingEntry = {
      name: this.cleanName(overrideName ?? this.nickname),
      score: Math.max(0, Math.round(score)),
    };
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!response.ok) {
        throw new Error('Failed to store score');
      }
      const payload = await response.json();
      const incoming = Array.isArray(payload.entries) ? payload.entries : [];
      this.entries = this.normalizeEntries(incoming);
      this.persistEntries();
      return {
        entries: this.getEntries(),
        placement: this.calculatePlacement(this.entries, entry),
      };
    } catch {
      const merged = this.mergeLocally(entry);
      this.entries = merged.entries;
      this.persistEntries();
      return {
        entries: this.getEntries(),
        placement: merged.placement,
      };
    }
  }

  private restoreFromStorage(): void {
    if (!this.hasStorage) {
      return;
    }
    const storedName = window.localStorage.getItem(STORAGE_KEYS.nickname);
    if (storedName) {
      this.nickname = storedName;
    }
    const cachedEntries = window.localStorage.getItem(STORAGE_KEYS.ranking);
    if (cachedEntries) {
      try {
        const parsed = JSON.parse(cachedEntries);
        if (Array.isArray(parsed)) {
          this.entries = this.normalizeEntries(parsed);
        }
      } catch {
        this.entries = [];
      }
    }
  }

  private persistNickname(): void {
    if (!this.hasStorage) {
      return;
    }
    window.localStorage.setItem(STORAGE_KEYS.nickname, this.nickname);
  }

  private persistEntries(): void {
    if (!this.hasStorage) {
      return;
    }
    window.localStorage.setItem(STORAGE_KEYS.ranking, JSON.stringify(this.entries));
  }

  private mergeLocally(entry: RankingEntry): { entries: RankingEntry[]; placement: number } {
    const combined = [...this.entries, entry].sort((a, b) => b.score - a.score);
    const placement = this.calculatePlacement(combined, entry);
    const clipped = combined.slice(0, MAX_ENTRIES);
    return { entries: clipped, placement };
  }

  private calculatePlacement(entries: RankingEntry[], entry: RankingEntry): number {
    const exactIndex = entries.findIndex(
      (candidate) => candidate.score === entry.score && candidate.name === entry.name,
    );
    if (exactIndex >= 0) {
      return exactIndex + 1;
    }
    let placement = 1;
    entries.forEach((candidate) => {
      if (candidate.score > entry.score) {
        placement += 1;
      }
    });
    return placement;
  }

  private normalizeEntries(entries: RankingEntry[]): RankingEntry[] {
    return entries
      .map((entry) => ({
        name: this.cleanName(entry.name),
        score: Math.max(0, Math.round(entry.score ?? 0)),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_ENTRIES);
  }

  private cleanName(raw: string): string {
    const trimmed = (raw ?? '').trim().slice(0, 16);
    return trimmed || 'Anonymous';
  }
}
